import React from "react";
import type { TypographyProps } from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";
import { Card, Typography, Button } from "@mui/material";
import { styled } from "@mui/material/styles";
import i18n from "../../i18n";

/** Icons on `StatCard` / paper metric tiles — always contrasts with `background.paper`. */
export function cardStatIconSx(fontSize: number) {
  return (theme: Theme) => ({
    fontSize,
    mb: 1,
    color: theme.palette.getContrastText(theme.palette.background.paper),
  });
}

/** Stat / metric tiles — align with global MuiCard (8px radius, 1px border, paper surface). */
export const StatCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(2.5),
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderRadius: theme.shape.borderRadius,
}));

export const EpisodeCard = styled(Card)(({ theme }) => ({
    borderRadius: theme.shape.borderRadius,
    overflow: "hidden",
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
}));

export const PlatformButton = styled(Button)(({ theme }) => ({
    padding: theme.spacing(1.5, 3),
    borderRadius: theme.shape.borderRadius,
    textTransform: "none",
    fontWeight: 600,
    fontSize: "1rem",
    minWidth: "200px",
    margin: theme.spacing(0.5),
})) as typeof Button;

// Typography on light (paper) surfaces
export const CardHeading = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.primary,
    fontWeight: 700,
    fontFamily: '"DM Sans", sans-serif',
}));

export const CardSecondaryText = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontFamily: '"DM Sans", sans-serif',
}));

// Typography on dark section backgrounds (e.g. platform headers). `data-mediakit-pdf-*` is for html2canvas PDF-only light overrides.
const DarkHeadingBase = styled(Typography)(() => ({
    color: "white",
    fontWeight: 700,
    fontFamily: '"DM Serif Display", serif',
}));

export const DarkHeading = React.forwardRef<HTMLElement, TypographyProps>(function DarkHeading(
    props,
    ref,
) {
    return <DarkHeadingBase ref={ref} data-mediakit-pdf-dark-heading="" {...props} />;
});

const DarkSecondaryTextBase = styled(Typography)(({ theme }) => ({
    color: alpha(theme.palette.common.white, 0.7),
    fontFamily: '"DM Sans", sans-serif',
}));

export const DarkSecondaryText = React.forwardRef<HTMLElement, TypographyProps>(
    function DarkSecondaryText(props, ref) {
        return <DarkSecondaryTextBase ref={ref} data-mediakit-pdf-dark-secondary="" {...props} />;
    },
);

// Section heading for analytics blocks
export const SectionHeading = styled(Typography)(({ theme }) => ({
    color: theme.palette.primary.main,
    fontWeight: 700,
    fontFamily: '"DM Serif Display", serif',
    fontSize: "1.25rem",
    marginBottom: "16px",
}));

export const formatNumber = (num: number): string => {
    if (num >= 1000000) {
        return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
        return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
};

export const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const locale = i18n.language?.startsWith("en") ? "en-US" : "es-ES";
    return date.toLocaleDateString(locale, {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
};

/**
 * Get consistent color for age range across both YouTube and Instagram sections
 */
export const getAgeRangeColor = (ageRange: string): string => {
    const cleanRange = ageRange.replace(/^age/i, "").trim();

    const ageColorMap: Record<string, string> = {
        "13-17": "#C94C4C",
        "18-24": "#F4C842",
        "25-34": "#00C49A",
        "35-44": "#1E9C5A",
        "45-54": "#4D9EF6",
        "55-64": "#6A89CC",
        "65-": "#A974F4",
        "65+": "#A974F4",
    };

    return ageColorMap[cleanRange] || ageColorMap["13-17"];
};
